import React, { useMemo, useState } from "react";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import EmptyItems from "@shared/components/EmptyItems";
import { formatDelayMs } from "../ResourceManager/Topology/utils/latencyMatrix";
import { LatencySite, SiteLatencyMatrixData } from "./request";
import {
    buildSiteLatencyLookup,
    formatSnapshotTime,
    shortSiteLabel,
    siteLabelParts,
    siteNeighbours,
} from "./siteLatencyLookup";
import SiteLatencyPanel, { SitePair } from "./SiteLatencyPanel";
import SiteNeighboursTable from "./SiteNeighboursTable";
import { useSiteLatencyMatrix } from "./useSiteLatencyMatrix";

/** The closest and farthest pair, shown before anything is selected. */
function findExtremes(
    data: SiteLatencyMatrixData,
    toPair: (fromId: string, toId: string) => SitePair | null
): { closest: SitePair | null; farthest: SitePair | null } {
    if (data.matrix.length === 0) return { closest: null, farthest: null };
    let min = data.matrix[0];
    let max = data.matrix[0];
    for (const entry of data.matrix) {
        if (entry.rttUs < min.rttUs) min = entry;
        if (entry.rttUs > max.rttUs) max = entry;
    }
    return {
        closest: toPair(min.from, min.to),
        farthest: toPair(max.from, max.to),
    };
}

/** Provider on top, site code below: narrow enough for horizontal headers. */
const SiteHeading: React.FC<{
    site: LatencySite;
    emphasised: boolean;
    centered?: boolean;
}> = ({ site, emphasised, centered = false }) => {
    const { provider, site: code } = siteLabelParts(site.label);
    return (
        <span
            className={`flex flex-col leading-tight ${centered ? "items-center" : ""}`}
            title={site.label}
        >
            {provider && (
                <span className="text-xs font-normal text-semantic-text-secondary">
                    {provider}
                </span>
            )}
            <span
                className={`text-sm ${
                    emphasised
                        ? "font-bold text-semantic-text-primary"
                        : "font-semibold text-semantic-text-secondary"
                }`}
            >
                {code}
            </span>
        </span>
    );
};

function describePair(pair: SitePair): string {
    const from = shortSiteLabel(pair.row.label);
    const to = shortSiteLabel(pair.col.label);
    return `${from} to ${to}: round trip ${formatDelayMs(pair.latency.rttUs)} ms`;
}

/**
 * Round-trip time between backbone sites (CTX-842), drawn as a distance
 * chart: RTT is symmetric, so each pair appears once, in the lower triangle.
 * The backend owns the numbers — lowest RTT among each site's routers,
 * along the IGP path — and leaves out the Extended PoP sites.
 */
const SiteLatencyMatrix: React.FC = () => {
    // Shared with the Overview map, so opening one after the other paints at once.
    const { data, error, isLoading } = useSiteLatencyMatrix();
    // Hover previews a pair; a click keeps it until another click.
    const [hovered, setHovered] = useState<SitePair | null>(null);
    const [selected, setSelected] = useState<SitePair | null>(null);
    // A phone lists the pairs instead of the matrix, which ran off the right
    // edge; slowest first, since a slow pair is what someone checks (CTX-1382).
    const isPhone = useWindowSize().width < 640;
    const [slowestFirst, setSlowestFirst] = useState(true);

    const pairOf = useMemo(() => {
        if (!data) return null;
        const lookup = buildSiteLatencyLookup(data);
        const sitesById = new Map(data.sites.map((site) => [site.id, site]));
        return (rowId: string, colId: string): SitePair | null => {
            const row = sitesById.get(rowId);
            const col = sitesById.get(colId);
            const latency = lookup(rowId, colId);
            return row && col && latency ? { row, col, latency } : null;
        };
    }, [data]);

    const extremes = useMemo(
        () =>
            data && pairOf
                ? findExtremes(data, pairOf)
                : { closest: null, farthest: null },
        [data, pairOf]
    );

    const neighbours = useMemo(
        () => (data ? siteNeighbours(data) : []),
        [data]
    );

    if (isLoading) {
        return (
            <div className="flex min-h-[160px] items-center justify-center text-sm text-semantic-text-secondary">
                Loading...
            </div>
        );
    }
    if (error || !data || !pairOf) {
        return <ErrorFetchingItems text="Error fetching latency between sites" />;
    }
    if (data.sites.length < 2) return <EmptyItems />;

    // Lower triangle: rows from the 2nd site on, columns up to the 2nd-to-last.
    const rowSites = data.sites.slice(1);
    const colSites = data.sites.slice(0, -1);
    const snapshot = formatSnapshotTime(data.measuredAt);

    const shown = hovered ?? selected;
    const isPair = (pair: SitePair | null, row: LatencySite, col: LatencySite) =>
        pair?.row.id === row.id && pair.col.id === col.id;
    const cellTone = (row: LatencySite, col: LatencySite) => {
        if (isPair(selected, row, col)) {
            return "bg-semantic-bg-brand-bold text-semantic-fg-base";
        }
        if (isPair(hovered, row, col)) {
            return "bg-semantic-bg-quaternary text-semantic-text-primary";
        }
        return "bg-semantic-bg-tertiary text-semantic-text-primary";
    };

    const renderCell = (row: LatencySite, col: LatencySite) => {
        const pair = pairOf(row.id, col.id);
        if (!pair) {
            return (
                <span className="block rounded-md py-2.5 text-center text-sm text-semantic-text-secondary">
                    —
                </span>
            );
        }
        const isSelected = isPair(selected, row, col);
        return (
            <button
                type="button"
                aria-label={describePair(pair)}
                aria-pressed={isSelected}
                onMouseEnter={() => setHovered(pair)}
                onFocus={() => setHovered(pair)}
                onClick={() => setSelected(isSelected ? null : pair)}
                className={`block min-w-[4rem] rounded-md px-2.5 py-2.5 text-center text-sm font-semibold tabular-nums outline-none transition-colors focus-visible:ring-2 focus-visible:ring-semantic-border-brand-primary ${cellTone(row, col)}`}
            >
                {formatDelayMs(pair.latency.rttUs)}
            </button>
        );
    };

    if (isPhone) {
        const pairs = rowSites.flatMap((row, rowIndex) =>
            colSites.slice(0, rowIndex + 1).flatMap((col) => {
                const pair = pairOf(row.id, col.id);
                return pair ? [pair] : [];
            })
        );
        pairs.sort((a, b) =>
            slowestFirst ? b.latency.rttUs - a.latency.rttUs : a.latency.rttUs - b.latency.rttUs
        );
        const slowest = Math.max(...pairs.map((pair) => pair.latency.rttUs), 1);
        return (
            <div className="flex flex-col gap-3">
                <p className="text-xs text-semantic-text-secondary">
                    Round-trip time in ms between sites, lowest across the routers
                    of each site.
                    {snapshot ? ` Measured ${snapshot}.` : ""}
                </p>
                <SiteLatencyPanel
                    active={selected}
                    closest={extremes.closest}
                    farthest={extremes.farthest}
                    isTouchList
                />
                <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-semantic-text-secondary">
                        {pairs.length} site pairs
                    </span>
                    <button
                        type="button"
                        onClick={() => setSlowestFirst((value) => !value)}
                        className="min-h-11 rounded-md border border-semantic-border-secondary px-3 text-sm font-semibold text-semantic-text-primary"
                    >
                        {slowestFirst ? "Slowest first" : "Fastest first"}
                    </button>
                </div>
                <ul className="divide-y divide-semantic-border-secondary">
                    {pairs.map((pair) => {
                        const isSelected = isPair(selected, pair.row, pair.col);
                        return (
                            <li key={`${pair.row.id}-${pair.col.id}`}>
                                <button
                                    type="button"
                                    aria-pressed={isSelected}
                                    aria-label={describePair(pair)}
                                    onClick={() => setSelected(isSelected ? null : pair)}
                                    className={`flex min-h-11 w-full flex-col gap-1.5 rounded-md px-2 py-2 text-left ${
                                        isSelected ? "bg-semantic-bg-brand-secondary" : ""
                                    }`}
                                >
                                    <span className="flex items-baseline justify-between gap-3">
                                        <span className="min-w-0 truncate text-sm text-semantic-text-primary">
                                            {shortSiteLabel(pair.row.label)} ↔ {shortSiteLabel(pair.col.label)}
                                        </span>
                                        <span className="shrink-0 text-sm font-semibold tabular-nums text-semantic-text-primary">
                                            {formatDelayMs(pair.latency.rttUs)} ms
                                        </span>
                                    </span>
                                    <span className="block h-1 w-full overflow-hidden rounded-full bg-semantic-bg-tertiary">
                                        <span
                                            className="block h-full rounded-full bg-semantic-bg-brand-bold"
                                            // Inline: a computed width class is invisible to the Tailwind scanner.
                                            style={{ width: `${(pair.latency.rttUs / slowest) * 100}%` }}
                                        />
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
                <SiteNeighboursTable rows={neighbours} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-xs text-semantic-text-secondary">
                Round-trip time in ms between sites, lowest across the routers
                of each site.
                {snapshot ? ` Measured ${snapshot}.` : ""}
            </p>

            <div className="grid items-start gap-8 lg:grid-cols-[max-content_minmax(0,1fr)]">
                <div className="overflow-x-auto">
                    {/* Compact on purpose: the width beside it goes to the
                        pair detail and the per-site table, not bigger cells. */}
                    <table
                        className="border-separate border-spacing-1.5"
                        onMouseLeave={() => setHovered(null)}
                        onBlur={() => setHovered(null)}
                    >
                        <thead>
                            <tr>
                                <th />
                                {colSites.map((col) => (
                                    <th
                                        key={col.id}
                                        scope="col"
                                        className="px-1 pb-1 text-center align-bottom"
                                    >
                                        <SiteHeading
                                            site={col}
                                            emphasised={shown?.col.id === col.id}
                                            centered
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rowSites.map((row, rowIndex) => (
                                <tr key={row.id}>
                                    <th
                                        scope="row"
                                        className="whitespace-nowrap pr-3 text-left"
                                    >
                                        <SiteHeading
                                            site={row}
                                            emphasised={shown?.row.id === row.id}
                                        />
                                    </th>
                                    {colSites.map((col, colIndex) => (
                                        <td key={col.id}>
                                            {colIndex <= rowIndex
                                                ? renderCell(row, col)
                                                : null}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                    <SiteLatencyPanel
                        active={shown}
                        closest={extremes.closest}
                        farthest={extremes.farthest}
                    />
                    <SiteNeighboursTable rows={neighbours} />
                </div>
            </div>
        </div>
    );
};

export default SiteLatencyMatrix;
